import React from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { CustomersTotal } from 'apis/merchant-gateway/insights/customers/types';
import { MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';

import { formatGrowthRate, getGrowthRateForFields, getGrowthTrend } from '../../utils';
import MetricBlock from '../OverallPerformance/MetricBlock';

type CustomerBaseSummaryProps = {
  customersTotalForSelected: CustomersTotal;
  customersTotalForPrevious: CustomersTotal | undefined;
  hideNewVsReturningInsights: boolean;
};

const Root = styled.div`
  display: flex;
  gap: 48px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-wrap: wrap;
    gap: 16px 32px;
  }
`;

const CustomerBaseSummary = ({
  customersTotalForSelected,
  customersTotalForPrevious,
  hideNewVsReturningInsights,
}: CustomerBaseSummaryProps) => {
  const { formatMessage, locale } = useIntl();

  const customersNumbersForSelected = {
    customersCount: customersTotalForSelected.customersCount ?? 0,
    firstTimeCustomersCount: customersTotalForSelected.firstTimeCustomersCount ?? 0,
    returningCustomersCount: customersTotalForSelected.returningCustomersCount ?? 0,
  };
  const customersNumbersForPrevious = customersTotalForPrevious
    ? {
        customersCount: customersTotalForPrevious.customersCount ?? 0,
        firstTimeCustomersCount: customersTotalForPrevious.firstTimeCustomersCount ?? 0,
        returningCustomersCount: customersTotalForPrevious.returningCustomersCount ?? 0,
      }
    : undefined;

  const growthRates =
    customersNumbersForPrevious &&
    getGrowthRateForFields(customersNumbersForSelected, customersNumbersForPrevious, [
      'customersCount',
      'firstTimeCustomersCount',
      'returningCustomersCount',
    ]);

  return (
    <Root>
      <MetricBlock
        title={formatMessage({ id: 'insights.customer-base.total-customers' })}
        tooltip={formatMessage({ id: 'insights.customer-base.total-customers-tooltip' })}
        metrics={customersNumbersForSelected.customersCount}
        growth={formatGrowthRate(growthRates?.customersCount, locale)}
        growthTrend={getGrowthTrend(growthRates?.customersCount)}
      />

      {!hideNewVsReturningInsights && (
        <>
          <MetricBlock
            title={formatMessage({ id: 'insights.customer-base.returning-customers' })}
            tooltip={formatMessage({ id: 'insights.customer-base.returning-customers-tooltip' })}
            metrics={customersNumbersForSelected.returningCustomersCount}
            growth={formatGrowthRate(growthRates?.returningCustomersCount, locale)}
            growthTrend={getGrowthTrend(growthRates?.returningCustomersCount)}
          />
          <MetricBlock
            title={formatMessage({ id: 'insights.customer-base.new-customers' })}
            tooltip={formatMessage({ id: 'insights.customer-base.new-customers-tooltip' })}
            metrics={customersNumbersForSelected.firstTimeCustomersCount}
            growth={formatGrowthRate(growthRates?.firstTimeCustomersCount, locale)}
            growthTrend={getGrowthTrend(growthRates?.firstTimeCustomersCount)}
          />
        </>
      )}
    </Root>
  );
};

export default CustomerBaseSummary;
